import React, { useMemo, useState } from 'react'
import { Card, Typography } from 'antd'
import { SupplierOrderHeader } from './SupplierOrderHeader'
import { SupplierOrderDetails } from './SupplierOrderDetails'
import { SupplierOrderItemList } from './PurchaseOrderDrawer'
import { useCompanyStore } from '../../store/company/companyStore'
import { useFFEStore } from '../../store/ffeStore'
import { calculateSupplierTotals } from '../../utils/purchaseOrders'
const { Text } = Typography

export const SupplierOrderList = () => {
  const company = useCompanyStore((state) => state.company)
  const { lineItems, materials, supplierCosts, settings } = useFFEStore(
    (state) => state.document
  )
  const [delectedIndexes, setDelectedIndexes] = useState<string[]>([])
  const onChangeItemSelection = (index: string, selected: boolean) => {
    if (!selected) {
      setDelectedIndexes((prev) => [...prev, index])
    } else {
      setDelectedIndexes((prev) => prev.filter((i) => i !== index))
    }
  }
  // Group items by supplier
  const supplierGroups = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        items: SupplierOrderItemList
      }
    >()

    // Group line items
    for (const item of lineItems) {
      if (
        item.supplier &&
        (item.status === 'specified' || item.status === 'clientApproved')
      ) {
        const group = groups.get(item.supplier) || { items: [] }
        group.items.push({
          ...item,
          type: 'lineItem',
          selected: !delectedIndexes.includes(item.id)
        })
        groups.set(item.supplier, group)
      }

      if (item.materials) {
        for (const materialLink of item.materials) {
          if (
            (materialLink.status !== 'specified' &&
              materialLink.status !== 'clientApproved') ||
            materialLink.quantity === 0
          ) {
            continue
          }

          const material = materials.find(
            (m) => m.id === materialLink.materialId
          )
          if (material && material.supplier) {
            const group = groups.get(material.supplier) || {
              items: []
            }
            group.items.push({
              ...material,
              type: 'material',
              status: materialLink.status,
              quantity: materialLink.quantity * item.quantity,
              id: materialLink.id,
              lineItem: item,
              materialId: material.id,
              selected: !delectedIndexes.includes(materialLink.id)
            })
            groups.set(material.supplier, group)
          }
        }
      }
    }

    // Also add delivery costs
    for (const supplierDelivery of supplierCosts) {
      const group = groups.get(supplierDelivery.supplierId)

      // Don't show delivery costs for suppliers with no items
      if (!group) {
        continue
      }
      group.items.push({
        type: 'lineItem',
        name: '',
        costPrice: supplierDelivery.deliveryCost ?? 0,
        sellPrice:
          (supplierDelivery.deliveryCost ?? 0) * (supplierDelivery.margin ?? 1),
        status: 'specified',
        quantity: 1,
        id: supplierDelivery.id,
        code: '',
        margin: ((supplierDelivery.margin ?? 1) - 1) * 100,
        supplierItemCode: 'Delivery',
        description: '',
        selected: !delectedIndexes.includes(supplierDelivery.id)
      })
      groups.set(supplierDelivery.supplierId, group)
    }

    return Array.from(groups.entries()).map(([supplier, data]) => ({
      supplierId: supplier,
      ...data,
      ...calculateSupplierTotals(data.items, {
        taxRate: company.settings.taxRate,
        taxInclusive: false
      }),
      taxRate: company.settings.taxRate
    }))
  }, [lineItems, materials, settings, delectedIndexes])

  const [collapsedSuppliers, setCollapsedSuppliers] = useState<Set<string>>(
    new Set(supplierGroups.map((group) => group.supplierId))
  )

  const supplierIdsInGroups = useMemo(() => {
    return supplierGroups.map((group) => group.supplierId)
  }, [supplierGroups])

  const toggleSupplier = (supplier: string) => {
    setCollapsedSuppliers((current) => {
      const newSet = new Set(current)
      if (newSet.has(supplier)) {
        newSet.delete(supplier)
      } else {
        newSet.add(supplier)
      }
      return newSet
    })
  }

  return (
    <div className="space-y-4">
      {supplierGroups.map((group) => (
        <Card
          key={group.supplierId}
          className="bg-white"
          styles={{ body: { padding: 0 } }}
        >
          <SupplierOrderHeader
            supplier={group.supplierId}
            items={group.items}
            isCollapsed={collapsedSuppliers.has(group.supplierId)}
            onToggleCollapse={() => toggleSupplier(group.supplierId)}
          />

          {!collapsedSuppliers.has(group.supplierId) && (
            <div className="p-4 border-t">
              <SupplierOrderDetails
                onChangeItemSelection={onChangeItemSelection}
                supplierIdsInGroups={supplierIdsInGroups}
                group={group}
              />
            </div>
          )}
        </Card>
      ))}
      {supplierGroups.length === 0 && (
        <Text type="secondary">
          No items or materials with suppliers found. Add suppliers to line
          items to generate purchase orders.
        </Text>
      )}
    </div>
  )
}
